import React, { useState, useEffect, useRef } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { renderMarkdown } from '../utils/markdown';

interface AppleViewProps {
  brand: string;
  piece: any;
  detail: any;
  onAction: (action: string, slug: string) => void;
  onToggleEngine: () => void;
  apiBase?: string;
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const STATUS_LABELS: Record<string, string> = {
  spark: 'Idea', research: 'Idea', plan: 'Idea', draft: 'Writing',
  review: 'Review', ready: 'Ready', published: 'Live',
};

const FORMAT_ICONS: Record<string, string> = {
  blog: '\u270E', medium: '\u24C2', 'social-card': '\u{1F5BC}', 'social-text': '\u{1F4AC}',
  reddit: '\u{1F4E8}', 'social-carousel': '\u{1F3A0}', 'linkedin-text': '\u{1F517}',
  'og-image': '\u{1F5BC}', 'ebook-html': '\u{1F4D6}', landing: '\u{1F310}',
  newsletter: '\u{2709}',
};

const FORMAT_NAMES: Record<string, string> = {
  // Legacy + cross-format renderers
  blog: 'Blog', 'blog-mdx': 'Blog (MDX)', medium: 'Medium',
  'social-card': 'Social Card', 'social-text': 'Social (text)',
  reddit: 'Reddit', 'social-carousel': 'Carousel',
  'linkedin-text': 'LinkedIn',
  'og-image': 'OG Image', 'ebook-html': 'Ebook', landing: 'Landing',
  newsletter: 'Newsletter',
  // LinkedIn Social Studio variants (each maps to a distinct LinkedIn
  // post template — Q&A, carousel deck, hook-story, listicle, hot-take, how-to)
  'linkedin-qna': 'Q&A',
  'linkedin-carousel': 'Carousel',
  'linkedin-hookstory': 'Hook + Story',
  'linkedin-listicle': 'Listicle',
  'linkedin-hottake': 'Hot Take',
  'linkedin-howto': 'How-to',
  // X (Twitter) variants
  'x-single': 'Single Tweet',
  'x-thread': 'Thread',
  'x-hottake': 'Hot Take',
  'x-listicle-thread': 'Listicle Thread',
};

const CHANNEL_NAMES: Record<string, string> = {
  // Blog distribution channels (#237): Site, Dev.to, Medium, Substack, Reddit.
  // linkedin / x are kept for Social Studio (separate "Create social post" flow),
  // not the blog publish bar. hashnode retained as a label for legacy renditions.
  site: 'Site', 'dev-to': 'Dev.to', medium: 'Medium', substack: 'Substack',
  reddit: 'Reddit', buttondown: 'Buttondown',
  linkedin: 'LinkedIn', x: 'X', hashnode: 'Hashnode',
};

// #285 B8: pipeline journey strip (additive — renders the previously-unused
// .journey styles at the top of the piece so progress is visible at a glance).
const JOURNEY_STAGES: { key: string; label: string }[] = [
  { key: 'ingest', label: 'Source' }, { key: 'research', label: 'Research' },
  { key: 'plan', label: 'Plan' }, { key: 'compose', label: 'Write' },
  { key: 'render', label: 'Render' }, { key: 'validate', label: 'Quality' },
];
const stageDone = (
  stages: Record<string, { outputHash?: string; status?: string } | undefined> | undefined,
  key: string,
): boolean => {
  const r = stages?.[key];
  return !!(r && (r.outputHash || r.status === 'completed'));
};

export default function AppleView({ brand, piece, detail, onAction, onToggleEngine, apiBase }: AppleViewProps) {
  const fileBase = apiBase ? `${apiBase}/file` : `/api/${brand}/pieces/${piece.slug}/file`;
  const [contentBody, setContentBody] = useState<string | null>(null);
  const [contentSource, setContentSource] = useState<'blog' | 'narrative' | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [activeFormat, setActiveFormat] = useState<string | null>(null);
  const [formatPreview, setFormatPreview] = useState<string | null>(null);
  // Blog editor: edit narrative.md → save (auto-commits) → re-render → republish.
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  async function startEdit() {
    setEditMsg('');
    try {
      const r = await fetch(`${fileBase}/narrative.md`);
      if (!r.ok) throw new Error('narrative.md not found — generate the piece first');
      setEditText(await r.text());
      setEditing(true);
    } catch (e) { setEditMsg(`Error: ${(e as Error).message}`); }
  }

  async function saveNarrative(rerender: boolean) {
    if (!editText.trim()) { setEditMsg('Error: content is empty'); return; }
    setEditBusy(true);
    setEditMsg('Saving…');
    try {
      const r = await fetch(`/api/${brand}/pieces/${piece.slug}/narrative`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editText }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || `save failed (HTTP ${r.status})`);
      setEditing(false);
      setEditMsg('Saved ✓');
      setReloadKey((k) => k + 1); // refetch the rendered preview
      setTimeout(() => setEditMsg(''), 4000);
      if (rerender) onAction('render:blog', piece.slug); // re-render the blog from the edited narrative
    } catch (e) { setEditMsg(`Error: ${(e as Error).message}`); }
    finally { setEditBusy(false); }
  }

  const title = piece.title || humanTitle(piece.slug);
  const renditions = piece.renditions || {};
  // Always fall back to the 3 standard formats so the tray is never empty
  const DEFAULT_FORMATS = ['blog', 'social-text', 'social-card'];
  const baseFormats: string[] =
    (detail?.configuredFormats?.length ? detail.configuredFormats : null) ??
    (Object.keys(renditions).length ? Object.keys(renditions) : null) ??
    DEFAULT_FORMATS;
  // Medium is an on-demand rendition (a lightly-restyled "Medium voice" page,
  // see engine/render/medium.ts), so it isn't in channels.yaml's pre-rendered
  // renditions list. Surface its card here so it's generatable before the
  // first render — without forcing the batch pipeline to build it for every
  // piece. Dedupe in case a medium rendition already exists.
  const configuredFormats: string[] = baseFormats.includes('medium')
    ? baseFormats
    : [...baseFormats, 'medium'];
  const publications = piece.publications || {};
  const narrativeHash = detail?.narrativeHash;
  const routing: Record<string, string[]> = detail?.routing || {};

  // Prefer evalScore (new SEO/GEO/Virality system) over old rendition score
  let worstScore: number | null = (piece as any).evalScore ?? null;
  let allPassed: boolean | null = worstScore !== null ? worstScore >= 85 : null;
  let warningCount = 0;
  if (worstScore === null) {
    for (const r of Object.values(renditions)) {
      if ((r as any).score != null) worstScore = worstScore === null ? (r as any).score : Math.min(worstScore!, (r as any).score);
      if ((r as any).passed != null) allPassed = allPassed === null ? (r as any).passed : (allPassed && (r as any).passed);
    }
  }
  if (detail?.validation?.warnings) warningCount = (detail.validation.warnings as string[]).length;

  // Freshness — content is stale when compose output has changed but render hasn't re-run
  // Compare render.inputHash with compose.outputHash: if they differ, renditions are stale
  const stages = piece.stages || {};
  const composeOutputHash = stages.compose?.outputHash;
  const renderInputHash = stages.render?.inputHash;
  const isStale = !!(composeOutputHash && renderInputHash && composeOutputHash !== renderInputHash);
  const freshLabel = isStale ? 'Source changed \u2014 refresh to update' : 'Up to date';

  // Pipeline state
  const isRunning = piece.pipelineStatus === 'running';

  // Destination summary
  const publishedCount = Object.keys(publications).length;
  const generatedCount = Object.keys(renditions).length;
  const notGenerated = configuredFormats.filter(f => !renditions[f]).length;

  // Load content preview: blog HTML first, fall back to narrative.md
  useEffect(() => {
    setContentBody(null);
    setContentSource(null);
    setExpanded(false);
    setActiveFormat(null);
    setFormatPreview(null);

    if (renditions.blog) {
      // Try blog rendition first (richest preview)
      fetch(`${fileBase}/renditions/blog.html`)
        .then(r => { if (!r.ok) throw new Error('no blog'); return r.text(); })
        .then(html => {
          const match = html.match(/<div class="article-body">([\s\S]*?)<\/div>\s*<\/article>/);
          setContentBody(match ? match[1] : html);
          setContentSource('blog');
        })
        .catch(() => loadNarrative());
    } else {
      loadNarrative();
    }

    function loadNarrative() {
      fetch(`${fileBase}/narrative.md`)
        .then(r => { if (!r.ok) throw new Error('no narrative'); return r.text(); })
        .then(md => {
          setContentBody(renderMarkdown(md, { brand, slug: piece.slug }));
          setContentSource('narrative');
        })
        .catch(() => {
          // No content at all — that's OK for early-stage pieces
          setContentBody(null);
          setContentSource(null);
        });
    }
  }, [piece.slug, brand, !!renditions.blog, reloadKey]);

  // #3 fix: Track blob URLs for format previews
  const formatBlobRef = useRef<string | null>(null);

  function loadFormatPreview(fmt: string) {
    if (activeFormat === fmt) {
      if (formatBlobRef.current) { URL.revokeObjectURL(formatBlobRef.current); formatBlobRef.current = null; }
      setActiveFormat(null); setFormatPreview(null); return;
    }
    // Revoke previous blob
    if (formatBlobRef.current) { URL.revokeObjectURL(formatBlobRef.current); formatBlobRef.current = null; }
    setActiveFormat(fmt);
    // Pipeline writes all renditions as .html — try that first, fall back to type-specific extensions
    const tryExts = ['.html', '.md', '.png'];
    async function tryFetch(exts: string[]): Promise<void> {
      for (const ext of exts) {
        try {
          const r = await fetch(`${fileBase}/renditions/${fmt}${ext}`);
          if (!r.ok) continue;
          if (ext === '.png') {
            const b = await r.blob();
            const url = URL.createObjectURL(b);
            formatBlobRef.current = url;
            setFormatPreview(url);
          } else {
            setFormatPreview(await r.text());
          }
          return;
        } catch { continue; }
      }
      setFormatPreview(null);
    }
    tryFetch(tryExts);
  }

  // Cleanup blob on unmount
  useEffect(() => {
    return () => { if (formatBlobRef.current) { URL.revokeObjectURL(formatBlobRef.current); formatBlobRef.current = null; } };
  }, []);

  return (
    <div className="apple-view">
      {/* Title + status */}
      <h1 className="apple-view__title">{title}</h1>
      <div className="apple-view__meta">
        <span className={`status-pill status-pill--${['spark','research','plan'].includes(piece.lifecycleState) ? 'idea' : piece.lifecycleState === 'draft' ? 'writing' : piece.lifecycleState === 'review' ? 'review' : piece.lifecycleState === 'ready' ? 'ready' : piece.lifecycleState === 'published' ? 'live' : 'idea'}`}>{STATUS_LABELS[piece.lifecycleState] || piece.lifecycleState}</span>
        <span className={`apple-view__fresh ${isStale ? 'apple-view__fresh--stale' : ''}`}>{isStale ? '\u26A0' : '\u2713'} {freshLabel}</span>
      </div>

      {/* #285 B8: pipeline journey strip — at-a-glance progress. Additive. */}
      <div className="journey" role="list" aria-label="Pipeline progress">
        {JOURNEY_STAGES.map((s, i) => {
          const done = stageDone(stages, s.key);
          // Connector left of step i is "done" when the step before it completed.
          const leftDone = i > 0 && stageDone(stages, JOURNEY_STAGES[i - 1].key);
          return (
            <React.Fragment key={s.key}>
              {i > 0 && <div aria-hidden="true" className={`journey__connector ${leftDone ? 'journey__connector--done' : ''}`} />}
              <div className={`journey__step journey__step--${done ? 'done' : 'pending'}`} role="listitem">
                <span aria-hidden="true">{done ? '\u2713' : '\u25CB'}</span> {s.label}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Blog editor — edit narrative.md, save (auto-commits), re-render + republish */}
      {editing && !activeFormat && (
        <div className="apple-view__content">
          <div className="apple-view__content-source" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Editing narrative.md</span>
            {editMsg && <span style={{ fontSize: 12, color: editMsg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)' }}>{editMsg}</span>}
          </div>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            spellCheck={false}
            style={{ width: '100%', minHeight: 420, boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.6, padding: 12, borderRadius: 0, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" disabled={editBusy} onClick={() => saveNarrative(true)}>Save &amp; re-render blog</button>
            <button className="btn btn-secondary" disabled={editBusy} onClick={() => saveNarrative(false)}>Save only</button>
            <button className="btn btn-secondary" disabled={editBusy} onClick={() => { setEditing(false); setEditMsg(''); }}>Cancel</button>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Saving auto-commits the narrative; then review &amp; publish from the channel bar below.</span>
          </div>
        </div>
      )}

      {/* Content preview — blog HTML or narrative markdown */}
      {!editing && contentBody && !activeFormat && (
        <div className="apple-view__content">
          <div className="apple-view__content-source" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{contentSource === 'narrative' ? 'Narrative preview' : 'Blog preview'}</span>
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '3px 10px' }} onClick={startEdit} title="Edit narrative.md, then re-render + republish">{'✎'} Edit</button>
          </div>
          <div
            className="article-body"
            style={!expanded ? { maxHeight: 360, overflow: 'hidden', maskImage: 'linear-gradient(to bottom, black 65%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black 65%, transparent)' } : undefined}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentBody) }}
          />
          {!expanded
            ? <button className="article-expand" onClick={() => setExpanded(true)}>Read full article...</button>
            : <button className="article-expand" onClick={() => setExpanded(false)}>Show less</button>
          }
        </div>
      )}
      {!editing && !contentBody && !activeFormat && generatedCount === 0 && (
        <div className="apple-view__empty-content">
          <p>No content yet. Run the pipeline to research, write, and render this piece.</p>
        </div>
      )}

      {/* Format preview (when a format is selected) */}
      {activeFormat && formatPreview && (
        <div className="apple-view__format-preview">
          <div className="apple-view__format-preview-bar">
            <span>{FORMAT_NAMES[activeFormat] || activeFormat}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* Regenerate the current format — issue #117. Re-runs the renderer
                  via the same render:format action used by the missing-rendition
                  click + FormatCard. */}
              <button
                className="btn btn-secondary btn--sm"
                data-testid={`apple-regenerate-${activeFormat}`}
                onClick={() => onAction(`render:${activeFormat}`, piece.slug)}
                title="Re-run the renderer for this format"
              >
                Regenerate
              </button>
              <button className="btn btn-secondary btn--sm" onClick={() => { setActiveFormat(null); setFormatPreview(null); }}>Back to article</button>
            </div>
          </div>
          {activeFormat.includes('card') || activeFormat.includes('image') || activeFormat.includes('carousel')
            ? <img src={formatPreview} alt={activeFormat} style={{ maxWidth: '100%', borderRadius: 0, margin: '16px 0' }} />
            : activeFormat.includes('html') || activeFormat === 'blog' || activeFormat === 'medium' || activeFormat === 'landing' || activeFormat === 'newsletter'
              ? <iframe srcDoc={formatPreview} style={{ width: '100%', minHeight: 400, border: 'none', borderRadius: 0, background: '#fff', margin: '16px 0' }} sandbox="allow-same-origin" />
              : <pre className="apple-view__format-text">{formatPreview}</pre>
          }
        </div>
      )}

      {/* Quality ring */}
      {worstScore !== null && (
        <div className="apple-view__quality">
          <div className={`apple-view__quality-ring ${worstScore >= 80 ? 'apple-view__quality-ring--good' : worstScore >= 60 ? 'apple-view__quality-ring--ok' : 'apple-view__quality-ring--bad'}`}>
            {worstScore}
          </div>
          <div className="apple-view__quality-info">
            <div className="apple-view__quality-label">
              Quality {allPassed ? 'passed' : 'needs work'}
            </div>
            {warningCount > 0 && (
              <div className="apple-view__quality-hint">
                {'\u26A0'} {warningCount} suggestion{warningCount > 1 ? 's' : ''} available
              </div>
            )}
          </div>
          <button className="btn btn-primary btn--sm"
            onClick={() => onAction('queue:recompose', piece.slug)}
            title="Queue LLM-driven rewrite for Claude Code">
            Recompose
          </button>
        </div>
      )}

      {/* Format tray */}
      <div className="apple-view__formats">
        <div className="apple-view__formats-label">Formats</div>

        {/* Pipeline running banner — replaces individual format actions while generating */}
        {isRunning && (
          <div className="apple-view__generating-banner">
            <span className="apple-view__generating-spinner">{'\u25CB'}</span>
            <span>Pipeline running{piece.runningStage ? ` \u2014 ${piece.runningStage}` : ''}...</span>
          </div>
        )}

        <div className="apple-view__format-tray">
          {configuredFormats.map(fmt => {
            const exists = !!renditions[fmt];
            const isActive = activeFormat === fmt;
            return (
              <button
                key={fmt}
                className={`apple-view__format-icon ${exists ? 'apple-view__format-icon--ready' : 'apple-view__format-icon--missing'} ${isActive ? 'apple-view__format-icon--active' : ''} ${isRunning && !exists ? 'apple-view__format-icon--generating' : ''}`}
                onClick={() => {
                  if (isRunning) return; // don't allow clicks while pipeline runs
                  if (exists) {
                    loadFormatPreview(fmt);
                  } else {
                    onAction(`render:${fmt}`, piece.slug);
                  }
                }}
                title={isRunning ? 'Pipeline running...' : exists ? `Preview ${FORMAT_NAMES[fmt] || fmt}` : `${FORMAT_NAMES[fmt] || fmt} \u2014 not yet generated`}
                disabled={isRunning}
              >
                <span className="apple-view__format-emoji">{FORMAT_ICONS[fmt] || '\u25A0'}</span>
                <span className="apple-view__format-name">{FORMAT_NAMES[fmt] || fmt}</span>
                {isRunning && !exists
                  ? <span className="apple-view__format-action apple-view__format-action--waiting">Waiting...</span>
                  : exists
                    ? <span className="apple-view__format-action">Preview</span>
                    : <span className="apple-view__format-action apple-view__format-action--generate">Generate</span>
                }
              </button>
            );
          })}
        </div>

        <div className="apple-view__formats-summary">
          {publishedCount > 0 && <span>{'\u2713'} {publishedCount} published</span>}
          {generatedCount - publishedCount > 0 && <span>{'\u25CB'} {generatedCount - publishedCount} ready</span>}
          {notGenerated > 0 && !isRunning && <span>{'\u25CB'} {notGenerated} not generated</span>}
        </div>
      </div>

      {/* Publish / Distribution \u2014 one row per channel.
         When multiple formats route to the same channel (e.g. X gets
         x-single / x-thread / x-hottake / x-listicle-thread), the user
         picks the type via a dropdown before publishing. */}
      {generatedCount > 0 && routing && Object.keys(routing).length > 0 && (
        <PublishSection
          routing={routing}
          publications={publications}
          slug={piece.slug}
          onAction={onAction}
        />
      )}

      {/* Actions */}
      <div className="apple-view__actions">
        {isRunning ? (
          <button className="btn btn-secondary" disabled>
            {'\u25CB'} Pipeline running{piece.runningStage ? ` \u2014 ${piece.runningStage}` : ''}...
          </button>
        ) : (
          <>
            <button className="btn btn-primary" onClick={() => onAction('pipeline', piece.slug)}>
              {generatedCount > 0 ? 'Rewrite Content' : 'Create Content'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── PublishSection ───────────────────────────────────────────────────────
//
// One row per channel. Inverts the brand-level routing
// (Record<format, channel[]>) into Record<channel, format[]> so a channel
// that accepts multiple format types (e.g. X with x-single / x-thread /
// x-hottake / x-listicle-thread) renders as ONE row with a type-selector
// dropdown. Channels with a single routed format render the format as
// static text — no need for a one-option dropdown.
//
// Preserves channel order from the original routing iteration so the
// blog → social → community grouping the brand config implies stays
// intact, without us having to encode the grouping rules here.

interface PublishSectionProps {
  routing: Record<string, string[]>;
  publications: Record<string, unknown>;
  slug: string;
  onAction: (action: string, slug: string) => void;
}

function PublishSection({ routing, publications, slug, onAction }: PublishSectionProps) {
  // Invert routing → channelToFormats while preserving first-seen order.
  // The `routing` prop is typed Record<string, string[]>, so iteration is
  // type-safe without casts.
  const channelToFormats: Record<string, string[]> = {};
  const channelOrder: string[] = [];
  for (const [fmt, channels] of Object.entries(routing)) {
    for (const ch of channels) {
      if (!channelToFormats[ch]) {
        channelToFormats[ch] = [];
        channelOrder.push(ch);
      }
      // Avoid duplicate format entries if a brand misconfigures routing.
      if (!channelToFormats[ch].includes(fmt)) channelToFormats[ch].push(fmt);
    }
  }

  // Per-channel selected format. Lazy initializer runs once on first mount.
  const [selectedByChannel, setSelectedByChannel] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const ch of channelOrder) init[ch] = channelToFormats[ch][0];
    return init;
  });

  // Seed defaults for any newly-appearing channel when the routing prop
  // changes after mount (e.g., the brand config is refreshed and adds a new
  // channel route). Existing user-picked defaults survive. Channels removed
  // from routing leave stale entries in state — harmless because we only
  // ever read `selectedByChannel[ch]` for channels currently in channelOrder.
  useEffect(() => {
    setSelectedByChannel((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const ch of channelOrder) {
        if (!(ch in next)) {
          next[ch] = channelToFormats[ch][0];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // channelOrder + channelToFormats are derived from routing on every
    // render; the effect only needs to re-run when routing itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routing]);

  // #285 B8: batch distribute — one click fires the SAME per-channel publish for
  // every not-yet-published channel (drafts). The per-channel rows + buttons
  // below are untouched; this is purely an addition.
  // Falsy test mirrors the per-channel row below (`const pub = publications[ch]`
  // then `pub ? Published : Publish`), so the batch set matches exactly what the
  // rows show as still-publishable.
  const unpublished = channelOrder.filter((ch) => !publications[ch]);
  const distributeAll = () => {
    for (const ch of unpublished) {
      const formats = channelToFormats[ch];
      if (!formats?.length) continue; // defensive: skip a channel with no formats
      const fmt = selectedByChannel[ch] ?? formats[0];
      onAction(formats.length > 1 ? `publish:${ch}:${fmt}` : `publish:${ch}`, slug);
    }
  };

  return (
    <div className="apple-view__publish">
      <div className="apple-view__formats-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span>Publish</span>
        {unpublished.length > 0 && (
          <button
            className="btn btn-primary btn--sm"
            onClick={distributeAll}
            title="Publish every not-yet-published channel as a draft, in one click"
          >
            {'\u2191'} Distribute all ({unpublished.length})
          </button>
        )}
      </div>
      {channelOrder.map((ch) => {
        const formats = channelToFormats[ch];
        const selectedFmt = selectedByChannel[ch] ?? formats[0];
        const pub = publications[ch] as { permalink?: string; publishedAt?: string } | undefined;
        const hasMultipleFormats = formats.length > 1;
        const showPreview = ch === 'dev-to' || ch === 'hashnode';

        return (
          <div key={ch} className="apple-view__publish-row apple-view__publish-row--clean">
            <span className="apple-view__publish-channel apple-view__publish-channel--clean">
              {CHANNEL_NAMES[ch] || ch}
            </span>
            <span className="apple-view__publish-type">
              {hasMultipleFormats ? (
                <select
                  className="apple-view__publish-select"
                  value={selectedFmt}
                  onChange={(e) => setSelectedByChannel({ ...selectedByChannel, [ch]: e.target.value })}
                  aria-label={`Select format for ${CHANNEL_NAMES[ch] || ch}`}
                >
                  {formats.map((f) => (
                    <option key={f} value={f}>{FORMAT_NAMES[f] || f}</option>
                  ))}
                </select>
              ) : (
                <span className="apple-view__publish-type-static">{FORMAT_NAMES[selectedFmt] || selectedFmt}</span>
              )}
            </span>
            <span className="apple-view__publish-actions">
              {pub ? (
                <>
                  <span className="apple-view__publish-status apple-view__publish-status--live">{'✓'} Published</span>
                  {pub.permalink && (
                    <a
                      href={pub.permalink}
                      target="_blank"
                      // noreferrer prevents leaking the dashboard URL to the
                      // destination (LinkedIn, X, etc.) as a Referer header.
                      rel="noopener noreferrer"
                      className="apple-view__publish-link"
                    >
                      View {'→'}
                    </a>
                  )}
                </>
              ) : (
                <>
                  {showPreview && (
                    <button
                      className="btn btn-secondary btn--sm"
                      onClick={() => onAction(`preview:${ch}`, slug)}
                      title={`Preview ${CHANNEL_NAMES[ch] || ch} version before saving as draft`}
                    >
                      Preview
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn--sm"
                    onClick={() => onAction(
                      // Only append the format segment when the user
                      // actually made a choice (i.e. a dropdown was shown).
                      // Single-format channels send the legacy `publish:${ch}`
                      // shape so the server hits its pre-PR per-channel
                      // logic — no behavior change for those rows.
                      hasMultipleFormats ? `publish:${ch}:${selectedFmt}` : `publish:${ch}`,
                      slug,
                    )}
                  >
                    Publish
                  </button>
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
